import { api } from '@/lib/api-client'
import { projectResultList } from '@/domain/results/candidate-view'
import type { TestResultListResponse } from '@/lib/types'

/** Server-side filters of `GET /tests/results/` (both already exist: `test_type=Full` matches tests with all four sections). */
export interface TestHistoryFilters {
  testType?: 'Full' | 'Listening' | 'Reading' | 'Writing' | 'Speaking'
  /** `completed` leaves abandoned attempts out, as the r5 Results list and Home do. */
  status?: 'completed'
}

export async function getTestHistory(limit = 10, offset = 0, filters: TestHistoryFilters = {}) {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (filters.testType) query.set('test_type', filters.testType)
  if (filters.status) query.set('status', filters.status)
  // Student payloads go through the candidate view (owner rule 1): no band that is not a band, no internal failure state.
  return projectResultList(await api.get<TestResultListResponse>(`/tests/results/?${query}`))
}

/**
 * One query owner for the student results list (PRODUCT-CONTRACT §4.3): the
 * `/my-exams` history page and the `/home` activity section both consume this
 * key, and the realtime `['results']` prefix invalidation reaches it.
 * The key stays under the `['results', 'history', {...}]` prefix so a filtered
 * list is invalidated with the rest; unfiltered callers keep their exact key.
 */
export function testHistoryQueryOptions(limit = 10, offset = 0, filters: TestHistoryFilters = {}) {
  const hasFilters = Boolean(filters.testType || filters.status)
  return {
    queryKey: ['results', 'history', hasFilters ? { limit, offset, ...filters } : { limit, offset }] as const,
    queryFn: () => getTestHistory(limit, offset, filters),
  }
}
